import { z } from 'zod';
import { daysBetween, today } from 'shared/src/date-string-utils/date-utils';
import type { ISODateString } from 'shared/src/date-string-utils/types';

const MicrosString = z.string().describe('Money amount in micros (1/1,000,000 of currency unit)');

export const CreateCampaignBudgetPayload = z.object({
  name: z.string().describe('Budget resource name visible in Google Ads UI'),
  amount_dollars: z
    .number()
    .describe('Daily budget amount in dollars (e.g. 50.00). Converted to micros at API call time.'),
  delivery_method: z
    .literal('STANDARD')
    .default('STANDARD')
    .describe(
      'Always STANDARD (spreads spend through the day). Google sunset ACCELERATED in 2019 and rejects it on create.'
    )
});
export type CreateCampaignBudgetPayload = z.infer<typeof CreateCampaignBudgetPayload>;

export const CampaignLocationTarget = z.object({
  location_text: z
    .string()
    .min(1)
    .describe(
      'Named location as plain text from the plan (city, state, or zip), e.g. "Springfield, IL" or "62704".'
    ),
  resolved_resource_name: z
    .string()
    .optional()
    .describe(
      'Google geo target constant resource name (geoTargetConstants/N). Resolved server-side at preview time — never set or invent this.'
    ),
  resolved_display_name: z
    .string()
    .optional()
    .describe(
      'Canonical Google location name shown on the preview card. Resolved server-side at preview time — never set this.'
    )
});
export type CampaignLocationTarget = z.infer<typeof CampaignLocationTarget>;

export const CampaignProximityTarget = z.object({
  address_text: z
    .string()
    .min(1)
    .describe('Address or place as plain text; Google geocodes it server-side.'),
  radius_miles: z.number().positive().describe('Radius around the address, in miles.')
});
export type CampaignProximityTarget = z.infer<typeof CampaignProximityTarget>;

export const CampaignGeoTargets = z.object({
  locations: z
    .array(CampaignLocationTarget)
    .optional()
    .describe('Named locations (city/state/zip as text) to target.'),
  proximities: z
    .array(CampaignProximityTarget)
    .optional()
    .describe('Radius targets ("within X miles of <address>").')
});
export type CampaignGeoTargets = z.infer<typeof CampaignGeoTargets>;

export const CreateCampaignPayload = z
  .object({
    name: z.string().describe('Campaign name'),
    advertising_channel_type: z
      .literal('SEARCH')
      .describe('Only SEARCH is supported in this adapter version.'),
    start_date: z.string().describe('Start date in YYYY-MM-DD (no time component)'),
    end_date: z.string().describe('End date in YYYY-MM-DD (no time component)'),
    bidding_strategy: z
      .union([
        z.literal('MANUAL_CPC'),
        z.literal('MAXIMIZE_CONVERSIONS'),
        z.literal('TARGET_CPA'),
        z.literal('TARGET_ROAS'),
        z.literal('MAXIMIZE_CLICKS')
      ])
      .describe('Google Ads bidding strategy keyword'),
    target_cpa_dollars: z
      .number()
      .positive()
      .optional()
      .describe(
        'Required when bidding_strategy is TARGET_CPA: the target cost-per-action in dollars.'
      ),
    target_roas: z
      .number()
      .positive()
      .optional()
      .describe(
        'Required when bidding_strategy is TARGET_ROAS: the target return on ad spend as a ratio (e.g. 3.0 = 300%). NOT a dollar amount.'
      ),
    max_cpc_dollars: z
      .number()
      .positive()
      .optional()
      .describe(
        'Optional Max CPC bid ceiling in dollars, valid ONLY with MAXIMIZE_CLICKS. Caps the ' +
          'highest cost-per-click the strategy will bid. Leave unset to let Google bid freely ' +
          '(the default — only set it when the client is CPC-sensitive).'
      ),
    tracking_template: z
      .string()
      .optional()
      .describe(
        'Optional tracking / UTM string pasted from the plan. If it contains the {lpurl} ' +
          'placeholder it is sent as the campaign tracking_url_template; otherwise it is treated ' +
          'as a query string and sent as final_url_suffix. Forward the card value verbatim.'
      ),
    geo_targets: CampaignGeoTargets.optional().describe(
      'Campaign-level geo targeting structured from the plan/Zoho geo text. Omit entirely when no geo is specified.'
    )
  })
  .superRefine((val, ctx) => {
    // The bidding-strategy keyword and its target value must agree: parameterised strategies
    // require their target, and non-parameterised strategies must not carry one. Rejecting here
    // (at parsePayload time) keeps a malformed pair from reaching the Google Ads API.
    if (val.bidding_strategy === 'TARGET_CPA') {
      if (val.target_cpa_dollars === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['target_cpa_dollars'],
          message: 'target_cpa_dollars is required when bidding_strategy is TARGET_CPA'
        });
      }
    } else if (val.target_cpa_dollars !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['target_cpa_dollars'],
        message: 'target_cpa_dollars is only valid when bidding_strategy is TARGET_CPA'
      });
    }
    if (val.bidding_strategy === 'TARGET_ROAS') {
      if (val.target_roas === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['target_roas'],
          message: 'target_roas is required when bidding_strategy is TARGET_ROAS'
        });
      }
    } else if (val.target_roas !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['target_roas'],
        message: 'target_roas is only valid when bidding_strategy is TARGET_ROAS'
      });
    }
    // Max CPC is a ceiling on the MAXIMIZE_CLICKS (target_spend) strategy. It is optional even
    // there (empty = let Google bid freely), so we only reject the meaningless pairing: a ceiling
    // attached to any other strategy.
    if (val.max_cpc_dollars !== undefined && val.bidding_strategy !== 'MAXIMIZE_CLICKS') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['max_cpc_dollars'],
        message: 'max_cpc_dollars is only valid when bidding_strategy is MAXIMIZE_CLICKS'
      });
    }
  });
export type CreateCampaignPayload = z.infer<typeof CreateCampaignPayload>;

export const CreateAdGroupPayload = z.object({
  name: z.string().describe('Ad group name'),
  type: z
    .literal('SEARCH_STANDARD')
    .describe('Only SEARCH_STANDARD ad groups are supported in this adapter version.'),
  cpc_bid_dollars: z
    .number()
    .describe('Default CPC bid in dollars (e.g. 1.50). Converted to micros at API call time.')
});
export type CreateAdGroupPayload = z.infer<typeof CreateAdGroupPayload>;

const MutateResult = z.object({
  resource_name: z.string()
});

export const GoogleAdsMutateResponse = z
  .object({
    results: z.array(MutateResult).optional(),
    partial_failure_error: z.unknown().optional()
  })
  .passthrough();
export type GoogleAdsMutateResponse = z.infer<typeof GoogleAdsMutateResponse>;

const RESOURCE_NAME_PATTERN = /\/(\d+)$/;

export function extractIdFromResourceName(resourceName: string): string {
  const match = RESOURCE_NAME_PATTERN.exec(resourceName);
  if (!match) {
    throw new Error(`Cannot extract numeric id from resource name '${resourceName}'`);
  }
  return match[1];
}

export function buildCampaignResourceName(customerId: string, campaignId: string): string {
  return `customers/${customerId}/campaigns/${campaignId}`;
}

export function buildCampaignBudgetResourceName(customerId: string, budgetId: string): string {
  return `customers/${customerId}/campaignBudgets/${budgetId}`;
}

export function buildAdGroupResourceName(customerId: string, adGroupId: string): string {
  return `customers/${customerId}/adGroups/${adGroupId}`;
}

// Google Ads displays customer IDs as 123-456-7890 but the API wants bare digits; accept either.
export function normalizeGoogleAdsCustomerId(raw: string): string {
  return raw.replace(/[\s-]/g, '');
}

// Customer IDs are always exactly 10 digits. Run on the normalized form.
export function isValidGoogleAdsCustomerId(customerId: string): boolean {
  return /^\d{10}$/.test(customerId);
}

export function dollarsToMicros(dollars: number): string {
  return MicrosString.parse(String(Math.round(dollars * 1_000_000)));
}

// Google Ads rejects any bid or budget whose micro amount is not a multiple of the billable unit
// (VALUE_NOT_MULTIPLE_OF_BILLABLE_UNIT). For USD the unit is 10,000 micros ($0.01), so every
// monetary value sent to the API must be rounded to the whole cent. Use this — not dollarsToMicros —
// for any amount that lands in a mutate operation (cpc_bid, target_cpa, max_cpc, budget amount).
export const BILLABLE_UNIT_MICROS = 10_000;

export function dollarsToBillableMicros(dollars: number): string {
  const micros = Math.round((dollars * 1_000_000) / BILLABLE_UNIT_MICROS) * BILLABLE_UNIT_MICROS;
  return MicrosString.parse(String(micros));
}

// Dollar-space twin of dollarsToBillableMicros: rounds a dollar amount to the whole cent (the USD
// billable unit). Use at preview time so the card shows — and the user confirms — exactly the bid
// that dollarsToBillableMicros will send, instead of a sub-cent value the API would reject.
export function roundDollarsToBillableUnit(dollars: number): number {
  return Math.round(dollars * 100) / 100;
}

export function microsToDollars(micros: string | number): number {
  const n = typeof micros === 'string' ? Number(micros) : micros;
  if (!Number.isFinite(n)) {
    throw new Error(`Invalid micros value '${micros}'`);
  }
  return n / 1_000_000;
}

export type DailyBudgetPacingBlock = { from: string; to: string; budget: number };

export type DailyBudgetResult = {
  amount_dollars: number;
  // The pacing block the daily amount was derived from (absent when there are no pacing blocks).
  source_block?: { from: string; to: string };
  // True when a later pacing block's daily amount differs from the chosen block's — Google Ads has
  // no native pacing-block automation, so the user must bump the budget when that block starts.
  later_blocks_differ: boolean;
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Pacing-block-aware daily budget. Google Ads carries a *daily* budget, while the plan carries a
 * lifetime total (optionally split into pacing blocks). When the line item has pacing blocks, the
 * daily budget is the current (or next-upcoming) block's amount spread over that block's inclusive
 * day count, and `later_blocks_differ` flags when a subsequent block's daily amount diverges so the
 * preview can warn the user. With no blocks, fall back to total ÷ inclusive flight length.
 *
 * Pure and `today`-injectable for testing. Dates are `YYYY-MM-DD`, so lexicographic comparison
 * matches chronological order.
 */
export function computeGoogleAdsDailyBudget(
  args: {
    total_price: number;
    start_date: string;
    end_date: string;
    pacing_blocks: DailyBudgetPacingBlock[];
  },
  todayDate: string = today()
): DailyBudgetResult {
  const blocks = [...args.pacing_blocks].sort((a, b) =>
    a.from < b.from ? -1 : a.from > b.from ? 1 : 0
  );
  if (blocks.length === 0) {
    const flightDays = daysBetween(
      args.start_date as ISODateString,
      args.end_date as ISODateString
    );
    return {
      amount_dollars: round2(args.total_price / Math.max(flightDays, 1)),
      later_blocks_differ: false
    };
  }
  const dailyOf = (b: DailyBudgetPacingBlock): number =>
    b.budget / Math.max(daysBetween(b.from as ISODateString, b.to as ISODateString), 1);
  const current = blocks.find(b => b.from <= todayDate && todayDate <= b.to);
  const upcoming = blocks.find(b => b.from >= todayDate);
  const chosen = current ?? upcoming ?? blocks[blocks.length - 1];
  const chosenDaily = dailyOf(chosen);
  const laterBlocksDiffer = blocks.some(
    b => b.from > chosen.from && round2(dailyOf(b)) !== round2(chosenDaily)
  );
  return {
    amount_dollars: round2(chosenDaily),
    source_block: { from: chosen.from, to: chosen.to },
    later_blocks_differ: laterBlocksDiffer
  };
}
